import { useId, useState } from 'react';
import { formatIstDate } from '@wa/shared';

export interface DayPoint {
  day: string;
  inbound: number;
  outbound: number;
}

const SERIES = [
  { key: 'inbound' as const, label: 'Received', colour: 'var(--chart-1)' },
  { key: 'outbound' as const, label: 'Sent', colour: 'var(--chart-2)' },
];

/**
 * Messages a day, in and out.
 *
 * A line and not bars: thirty daily points is a shape, and the question a client
 * asks of it is "is this going up" rather than "how many exactly on the 14th" —
 * which the tooltip and the numbers table answer when it is asked.
 *
 * One axis, deliberately. Received and sent are the same measure in the same unit,
 * so they share a scale; a second axis would let the two lines cross wherever the
 * scales happened to put them and mean nothing at all.
 *
 * The colours are not the brand teal. #0F6B55 has an OKLCH chroma of 0.088, under
 * the floor a data mark needs, and as a thin line on white it reads grey — measured
 * rather than eyeballed, along with the deutan and tritan separation of the pair
 * that replaced it.
 */
export function VolumeChart({ days }: { days: DayPoint[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const [showNumbers, setShowNumbers] = useState(false);
  const titleId = useId();

  if (days.length === 0) {
    return (
      <p className="px-3 py-8 text-center text-small text-(--color-muted) sm:px-4">
        Nothing in this range yet. Figures appear the morning after each day.
      </p>
    );
  }

  // A fixed viewBox scaled by CSS. The chart has to work at 360px and at 1600, and
  // an SVG that redraws itself on every resize is a lot of machinery for a shape
  // that is identical at both.
  const W = 720;
  const H = 220;
  const PAD = { top: 12, right: 12, bottom: 26, left: 40 };

  const peak = Math.max(1, ...days.flatMap((d) => [d.inbound, d.outbound]));
  const ceiling = niceCeiling(peak);

  const x = (i: number) =>
    PAD.left + (days.length === 1 ? 0 : (i * (W - PAD.left - PAD.right)) / (days.length - 1));
  const y = (v: number) => H - PAD.bottom - (v / ceiling) * (H - PAD.top - PAD.bottom);

  const path = (key: 'inbound' | 'outbound') =>
    days.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d[key]).toFixed(1)}`).join(' ');

  const ticks = [0, ceiling / 2, ceiling];
  const point = hover === null ? null : days[hover];

  return (
    <div className="flex flex-col gap-2">
      {/* The legend is always present with two series, so identity never rests on
          colour alone. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 sm:px-4">
        {SERIES.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5 text-micro text-(--color-muted)">
            <span aria-hidden="true" className="h-0.5 w-4 rounded-full" style={{ background: s.colour }} />
            {s.label}
          </span>
        ))}

        <button
          type="button"
          onClick={() => setShowNumbers((v) => !v)}
          aria-expanded={showNumbers}
          className="tap ml-auto rounded-(--radius-chip) px-2 py-0.5 text-micro text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)"
        >
          {showNumbers ? 'Hide numbers' : 'Show numbers'}
        </button>
      </div>

      <div className="relative px-1 sm:px-2">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="h-auto w-full"
          role="img"
          aria-labelledby={titleId}
          onMouseLeave={() => setHover(null)}
        >
          <title id={titleId}>
            Messages received and sent per day, {formatIstDate(days[0]!.day)} to{' '}
            {formatIstDate(days[days.length - 1]!.day)}.
          </title>

          {/* Recessive grid: it orients the eye and does not compete with the data. */}
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)}
                stroke="var(--color-line-dim)" strokeWidth="1"
              />
              <text
                x={PAD.left - 6} y={y(t) + 3} textAnchor="end"
                className="fill-(--color-faint)" style={{ fontSize: 10 }}
              >
                {t >= 1000 ? `${Math.round(t / 1000)}k` : Math.round(t)}
              </text>
            </g>
          ))}

          {SERIES.map((s) => (
            <path
              key={s.key}
              d={path(s.key)}
              fill="none"
              stroke={s.colour}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}

          {/* The crosshair, and a marker on each series at the hovered day. */}
          {hover !== null && (
            <>
              <line
                x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={H - PAD.bottom}
                stroke="var(--color-line)" strokeWidth="1"
              />
              {SERIES.map((s) => (
                <circle
                  key={s.key}
                  cx={x(hover)} cy={y(days[hover]![s.key])} r="4"
                  fill={s.colour}
                  // A ring in the surface colour, so two markers that land on top of
                  // each other still read as two.
                  stroke="var(--color-surface)" strokeWidth="2"
                />
              ))}
            </>
          )}

          {/* Hit targets wider than the marks. A four-pixel dot is not something to
              ask somebody to hit with a finger. */}
          {days.map((d, i) => (
            <rect
              key={d.day}
              x={x(i) - (W - PAD.left - PAD.right) / Math.max(days.length * 2, 2)}
              y={PAD.top}
              width={Math.max((W - PAD.left - PAD.right) / Math.max(days.length, 1), 8)}
              height={H - PAD.top - PAD.bottom}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
            />
          ))}

          {/* First, middle and last only. A label under every point is a smear. */}
          {[0, Math.floor(days.length / 2), days.length - 1]
            .filter((i, n, all) => all.indexOf(i) === n && days[i] !== undefined)
            .map((i) => (
              <text
                key={i}
                x={x(i)} y={H - 8}
                textAnchor={i === 0 ? 'start' : i === days.length - 1 ? 'end' : 'middle'}
                className="fill-(--color-faint)" style={{ fontSize: 10 }}
              >
                {formatIstDate(days[i]!.day)}
              </text>
            ))}
        </svg>

        {point && (
          <div
            role="status"
            className="pointer-events-none absolute top-1 right-2 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2 py-1 text-micro shadow-sm"
          >
            <p className="font-medium">{formatIstDate(point.day)}</p>
            {SERIES.map((s) => (
              <p key={s.key} className="flex items-center gap-1.5 text-(--color-muted)">
                <span aria-hidden="true" className="size-1.5 rounded-full" style={{ background: s.colour }} />
                {s.label} <span className="tabular-nums text-(--color-ink)">{point[s.key].toLocaleString('en-IN')}</span>
              </p>
            ))}
          </div>
        )}
      </div>

      {/* The table view. Not a nicety: a chart that cannot be read as numbers is a
          chart somebody has to take on trust. */}
      {showNumbers && (
        <div className="overflow-x-auto px-3 pb-2 sm:px-4">
          <table className="w-full min-w-80 text-small">
            <thead>
              <tr className="text-left text-micro uppercase tracking-wide text-(--color-faint)">
                <th className="py-1 pr-3 font-medium">Day</th>
                <th className="py-1 pr-3 text-right font-medium">Received</th>
                <th className="py-1 text-right font-medium">Sent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-(--color-line-dim)">
              {days.map((d) => (
                <tr key={d.day}>
                  <td className="py-1 pr-3 whitespace-nowrap">{formatIstDate(d.day)}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{d.inbound.toLocaleString('en-IN')}</td>
                  <td className="py-1 text-right tabular-nums">{d.outbound.toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** A round number above the peak, so the top gridline reads as a figure. */
function niceCeiling(peak: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(peak));
  return Math.ceil(peak / magnitude) * magnitude;
}
